import {
  ShoppingCartDto,
  ApiShoppingCartsGuidGetRequest,
  ApiShoppingCartsActivateGuidPatchRequest,
  ApiShoppingCartsCancelQuotationRequestGuidPatchRequest,
  ApiShoppingCartsGuidNamePatchRequest,
  ApiShoppingCartsGuidRejectQuotePatchRequest,
  ApiShoppingCartsItemsPutRequest,
  ApiShoppingCartsRequestOrderApprovalPostRequest,
  ApiShoppingCartsPostRequest,
  ApiShoppingCartsPriceListPatchRequest,
  ApiShoppingCartsRequestQuotationPatchRequest,
  ApiShoppingCartsShoppingCartItemIdAdditionalInformationPatchRequest,
  ApiShoppingCartsShoppingCartItemIdDeleteRequest,
  ShoppingCartItemAdditionalInformationUpdatedDto,
  ShoppingCartNameUpdatedDto,
  ShoppingCartUpdatedDto,
  ApiShoppingCartsGuidAttachmentTypePostRequest,
  ApiShoppingCartsGuidAttachmentAttachmentIdDeleteRequest,
  AttachmentCreatedDto,
  SalesChannelDto,
  ApiShoppingCartsPlaceOrderWithoutApprovalPostRequest,
  ApiShoppingCartsGuidCreateQuotePutRequest,
  QuoteDto,
  ApiShoppingCartsManageQuotesIdGetRequest,
  NotificationSeverity,
  ApiShoppingCartsApproveQuotePatchRequest,
  ApiShoppingCartsGuidRejectApprovalPatchRequest,
  ApiShoppingCartsGuidPlaceOrderFromWaitingForApprovalCommandPatchRequest,
  ApiShoppingCartsTermsOfDeliveriesGetRequest,
  TermsOfDeliveryDto,
  ApiShoppingCartsGuidRequestQuoteApprovalPostRequest,
  ApiShoppingCartsGuidSendQuoteWithoutApprovalPostRequest,
  ShoppingCartItemSoftwarePurchaseEmailUpdatedDto,
  ApiShoppingCartsShoppingCartItemIdSoftwarePurchaseEmailPatchRequest
} from "api";
import {
  createGenericSaga,
  CreateSagaOptions,
  defaultSagaErrorHandler,
  defaultSagaSuccessHandler,
  GenericSagaOptions,
  SagaBuilder
} from "../../../framework/sagas/sagaUtilities";
import { getApiRegistry } from "../../../framework/state/apiRegistry";
import { userActions } from "../actions/userActions";

interface ShoppingCartSagas {
  // CreateSaga accepts only this, not sure how to type but this works
  [index: string]: CreateSagaOptions<any, any>;
  getShoppingCartById: GenericSagaOptions<ShoppingCartDto, ApiShoppingCartsGuidGetRequest>;
  addOrUpdateItemsInShoppingCart: GenericSagaOptions<void, ApiShoppingCartsItemsPutRequest>;
  createShoppingCart: GenericSagaOptions<ShoppingCartUpdatedDto, ApiShoppingCartsPostRequest>;
  changeShoppingCartPriceList: GenericSagaOptions<void, ApiShoppingCartsPriceListPatchRequest>;
  deleteShoppingCartItem: GenericSagaOptions<void, ApiShoppingCartsShoppingCartItemIdDeleteRequest>;
  updateShoppingCartName: GenericSagaOptions<
    ShoppingCartNameUpdatedDto,
    ApiShoppingCartsGuidNamePatchRequest
  >;
  updateShoppingCartItemAdditionalInformation: GenericSagaOptions<
    ShoppingCartItemAdditionalInformationUpdatedDto,
    ApiShoppingCartsShoppingCartItemIdAdditionalInformationPatchRequest
  >;
  updateShoppingCartItemSoftwarePurchaseEmail: GenericSagaOptions<
    ShoppingCartItemSoftwarePurchaseEmailUpdatedDto,
    ApiShoppingCartsShoppingCartItemIdSoftwarePurchaseEmailPatchRequest
  >;
  requestOrderApproval: GenericSagaOptions<void, ApiShoppingCartsRequestOrderApprovalPostRequest>;
  requestQuotation: GenericSagaOptions<void, ApiShoppingCartsRequestQuotationPatchRequest>;
  cancelQuotationRequest: GenericSagaOptions<
    void,
    ApiShoppingCartsCancelQuotationRequestGuidPatchRequest
  >;
  activateShoppingCart: GenericSagaOptions<
    ShoppingCartUpdatedDto,
    ApiShoppingCartsActivateGuidPatchRequest
  >;
  rejectQuote: GenericSagaOptions<void, ApiShoppingCartsGuidRejectQuotePatchRequest>;
  uploadAttachment: GenericSagaOptions<
    AttachmentCreatedDto,
    ApiShoppingCartsGuidAttachmentTypePostRequest
  >;
  deleteAttachment: GenericSagaOptions<
    string,
    ApiShoppingCartsGuidAttachmentAttachmentIdDeleteRequest
  >;
  getSalesChannels: GenericSagaOptions<Array<SalesChannelDto>, RequestInit | undefined>;
  placeOrderWithoutApproval: GenericSagaOptions<
    void,
    ApiShoppingCartsPlaceOrderWithoutApprovalPostRequest
  >;
  createQuote: GenericSagaOptions<void, ApiShoppingCartsGuidCreateQuotePutRequest>;
  getQuoteById: GenericSagaOptions<QuoteDto, ApiShoppingCartsManageQuotesIdGetRequest>;
  requestQuoteApproval: GenericSagaOptions<
    number,
    ApiShoppingCartsGuidRequestQuoteApprovalPostRequest
  >;
  approveOrderOrQuote: GenericSagaOptions<void, ApiShoppingCartsApproveQuotePatchRequest>;
  sendQuoteWithoutApproval: GenericSagaOptions<
    number,
    ApiShoppingCartsGuidSendQuoteWithoutApprovalPostRequest
  >;
  rejectApproval: GenericSagaOptions<void, ApiShoppingCartsGuidRejectApprovalPatchRequest>;
  placeOrder: GenericSagaOptions<
    void,
    ApiShoppingCartsGuidPlaceOrderFromWaitingForApprovalCommandPatchRequest
  >;
  getTermsOfDeliveries: GenericSagaOptions<
    TermsOfDeliveryDto[],
    ApiShoppingCartsTermsOfDeliveriesGetRequest
  >;
}

export const shoppingCartSagas: ShoppingCartSagas = {
  addOrUpdateItemsInShoppingCart: createGenericSaga(
    "shoppingCartSagas/addOrUpdateItemsInShoppingCart",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsItemsPut.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onSuccess: (request) =>
        request.addOrUpdateShoppingCartItemsCommand
          ? new SagaBuilder()
              .dispatch(
                shoppingCartSagas.getShoppingCartById.createAction({
                  guid: request.addOrUpdateShoppingCartItemsCommand.shoppingCartGuid
                })
              )
              .build()()
          : undefined,
      onFail: defaultSagaErrorHandler
    }
  ),
  createShoppingCart: createGenericSaga(
    "shoppingCartSagas/createShoppingCart",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsPost.bind(getApiRegistry().shoppingCartsApi),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  getShoppingCartById: createGenericSaga(
    "shoppingCartSagas/getShoppingCartById",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidGet.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  changeShoppingCartPriceList: createGenericSaga(
    "shoppingCartSagas/changeShoppingCartPriceList",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsPriceListPatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onSuccess: (request) =>
        request.updateShoppingCartPriceListCommand
          ? new SagaBuilder()
              .dispatch(
                shoppingCartSagas.getShoppingCartById.createAction({
                  guid: request.updateShoppingCartPriceListCommand?.shoppingCartGuid
                })
              )
              .build()()
          : undefined,
      onFail: defaultSagaErrorHandler
    }
  ),
  deleteShoppingCartItem: createGenericSaga(
    "shoppingCartSagas/deleteShoppingCartItem",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsShoppingCartItemIdDelete.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  updateShoppingCartName: createGenericSaga(
    "shoppingCartSagas/updateShoppingCartName",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidNamePatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  updateShoppingCartItemAdditionalInformation: createGenericSaga(
    "shoppingCartSagas/updateShoppingCartItemAdditionalInformation",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsShoppingCartItemIdAdditionalInformationPatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  updateShoppingCartItemSoftwarePurchaseEmail: createGenericSaga(
    "shoppingCartSagas/updateShoppingCartItemSoftwarePurchaseEmail",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsShoppingCartItemIdSoftwarePurchaseEmailPatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      onSuccess: defaultSagaSuccessHandler("Email updated"),
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  requestOrderApproval: createGenericSaga(
    "shoppingCartSagas/requestOrderApproval",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsRequestOrderApprovalPost.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      onSuccess: (request) => {
        return new SagaBuilder()
          .dispatch(
            shoppingCartSagas.getShoppingCartById.createAction({
              guid: request.requestOrderApprovalCommand?.identifier ?? ""
            })
          )
          .build()();
      },
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  requestQuotation: createGenericSaga(
    "shoppingCartSagas/requestQuotation",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsRequestQuotationPatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  cancelQuotationRequest: createGenericSaga(
    "shoppingCartSagas/cancelQuotationRequest",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsCancelQuotationRequestGuidPatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      onSuccess: (request) =>
        new SagaBuilder()
          .dispatch(shoppingCartSagas.getShoppingCartById.createAction({ guid: request.guid }))
          .build()(),
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  activateShoppingCart: createGenericSaga(
    "shoppingCartSagas/activateShoppingCart",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsActivateGuidPatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  rejectQuote: createGenericSaga(
    "shoppingCartSagas/rejectQuote",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidRejectQuotePatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onSuccess: (request) =>
        new SagaBuilder()
          .dispatch(shoppingCartSagas.getShoppingCartById.createAction({ guid: request.guid }))
          .build()(),
      onFail: defaultSagaErrorHandler
    }
  ),
  uploadAttachment: createGenericSaga(
    "shoppingCartSagas/uploadAttachment",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidAttachmentTypePost.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  deleteAttachment: createGenericSaga(
    "shoppingCartSagas/deleteAttachment",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidAttachmentAttachmentIdDelete.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  getSalesChannels: createGenericSaga(
    "shoppingCartSagas/getSalesChannels",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsSalesChannelsGet.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  placeOrderWithoutApproval: createGenericSaga(
    "shoppingCartSagas/placeOrderWithoutApproval",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsPlaceOrderWithoutApprovalPost.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      onSuccess: (request) => {
        return new SagaBuilder()
          .dispatch(
            shoppingCartSagas.getShoppingCartById.createAction({
              guid: request.placeOrderWithoutApprovalCommand?.identifier ?? ""
            })
          )
          .build()();
      },
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  getQuoteById: createGenericSaga(
    "shoppingCartSagas/getQuoteById",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsManageQuotesIdGet.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  createQuote: createGenericSaga(
    "shoppingCartSagas/createQuote",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidCreateQuotePut.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onSuccess: (request) =>
        request
          ? new SagaBuilder()
              .dispatch(
                userActions.addNotification(
                  NotificationSeverity.Success,
                  "Created quote successfully"
                )
              )
              .build()()
          : undefined,
      onFail: defaultSagaErrorHandler
    }
  ),
  requestQuoteApproval: createGenericSaga(
    "shoppingCartSagas/requestQuoteApproval",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidRequestQuoteApprovalPost.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onSuccess: (request) => {
        const builder = new SagaBuilder();
        builder.dispatch(
          userActions.addNotification(
            NotificationSeverity.Success,
            "Requested quote approval successfully"
          )
        );
        if (request.saveQuoteDto.quoteReceiverEmail != null) {
          builder.dispatch(userActions.updateSelectedShoppingCartGuid(null));
        }
        return builder.build()();
      },
      onFail: defaultSagaErrorHandler
    }
  ),
  approveOrderOrQuote: createGenericSaga(
    "shoppingCartSagas/approveOrderOrQuote",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsApproveQuotePatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onSuccess: defaultSagaSuccessHandler("Approved"),
      onFail: defaultSagaErrorHandler
    }
  ),
  sendQuoteWithoutApproval: createGenericSaga(
    "shoppingCartSagas/sendQuoteWithoutApproval",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidSendQuoteWithoutApprovalPost.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onSuccess: (request) => {
        const builder = new SagaBuilder();
        builder.dispatch(
          userActions.addNotification(NotificationSeverity.Success, "Quote sent successfully")
        );
        if (request.saveQuoteDto.quoteReceiverEmail != null) {
          builder.dispatch(userActions.updateSelectedShoppingCartGuid(null));
        }
        return builder.build()();
      },
      onFail: defaultSagaErrorHandler
    }
  ),
  rejectApproval: createGenericSaga(
    "shoppingCartSagas/rejectApproval",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidRejectApprovalPatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      onSuccess: defaultSagaSuccessHandler("Approval rejected"),
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  placeOrder: createGenericSaga(
    "shoppingCartSagas/placeOrder",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsGuidPlaceOrderFromWaitingForApprovalCommandPatch.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  ),
  getTermsOfDeliveries: createGenericSaga(
    "shoppingCartSagas/getTermsOfDeliveries",
    getApiRegistry().shoppingCartsApi.apiShoppingCartsTermsOfDeliveriesGet.bind(
      getApiRegistry().shoppingCartsApi
    ),
    {
      takeEvery: false,
      onFail: defaultSagaErrorHandler
    }
  )
};
